import { useEffect, useRef } from "react";

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

function loadScript() {
  if (window.turnstile) return Promise.resolve();
  if (window.__turnstileLoading) return window.__turnstileLoading;
  window.__turnstileLoading = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true;
    s.defer = true;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return window.__turnstileLoading;
}

export const Turnstile = ({ onVerify, onExpire, onError }) => {
  const ref = useRef(null);
  const widgetId = useRef(null);

  useEffect(() => {
    let mounted = true;
    // If the widget hasn't verified within this window (script blocked, domain not
    // allow-listed, network issue), tell the parent so it can degrade gracefully.
    const failTimer = setTimeout(() => { if (mounted) onError && onError(); }, 9000);
    let verified = false;

    if (!SITE_KEY) {
      clearTimeout(failTimer);
      onError && onError();
      return () => { mounted = false; };
    }

    loadScript()
      .then(() => {
        if (!mounted || !ref.current || !window.turnstile) return;
        widgetId.current = window.turnstile.render(ref.current, {
          sitekey: SITE_KEY,
          theme: "dark",
          callback: (token) => { verified = true; clearTimeout(failTimer); onVerify && onVerify(token); },
          "expired-callback": () => onExpire && onExpire(),
          "error-callback": () => { if (!verified) { clearTimeout(failTimer); onError && onError(); } },
        });
      })
      .catch(() => { clearTimeout(failTimer); onError && onError(); });

    return () => {
      mounted = false;
      clearTimeout(failTimer);
      if (widgetId.current != null && window.turnstile) {
        try { window.turnstile.remove(widgetId.current); } catch { /* noop */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div ref={ref} data-testid="turnstile-widget" />;
};

export default Turnstile;
